import { t } from '../../../i18n/i18n'
import { PAGE_MODE, type PageMode } from '../../../shared/constants'
import { ErrorService } from '../../../shared/service/errorService'
import { SnackbarService } from '../../../shared/snackbar/snackbar.service'
import { create } from '../../../shared/utils/zustandVue'
import { cloneDeep, fromEntries, isNotNullish } from '../../../utils'
import { MapService } from '../map.service'
import { DEFAULT_MAP, type MapObject } from '../map.type'

class MapsState {
  mode: PageMode = PAGE_MODE.VIEW
  maps: Record<string, MapObject> = {}
  current: MapObject | null = null
  loading = false
}

type MapsStore = MapsState & {
  setAdding: () => void
  setEditing: (payload: MapObject) => void
  load: () => void
  unload: () => void
  setDeleting: (payload: MapObject) => void
  setView: () => void
  setMaps: (payload: MapObject[]) => void
  clearCurrent: () => void
  list: () => Promise<void>
  editMap: (payload: MapObject) => Promise<void>
  addMap: (payload: MapObject) => Promise<void>
  delete: () => Promise<void>
  toggleVisibility: (payload: MapObject) => Promise<void>
  reset: () => void
}

export const useMapsStore = create<MapsStore>((set, get) => ({
  ...new MapsState(),

  setAdding: () => {
    set({ mode: PAGE_MODE.ADD, current: cloneDeep(DEFAULT_MAP) })
  },
  setEditing: (payload) => {
    set({ mode: PAGE_MODE.EDIT, current: cloneDeep(payload) })
  },
  load: () => {
    set({ loading: true })
  },
  unload: () => {
    set({ loading: false })
  },
  setDeleting: (payload) => {
    set({ mode: PAGE_MODE.DELETE, current: cloneDeep(payload) })
  },
  setView: () => {
    set({ mode: PAGE_MODE.VIEW, current: null, loading: false })
  },
  setMaps: (payload) => {
    set({
      maps: fromEntries(
        payload
          .map((item) =>
            item._id != null ? ([item._id, item] as const) : null,
          )
          .filter(isNotNullish),
      ),
      loading: false,
    })
  },
  clearCurrent: () => {
    set({ current: null, loading: false })
  },

  list: async () => {
    try {
      get().load()
      get().setMaps(await MapService.getMapList())
    } catch (e) {
      ErrorService.handleError(e)
    }
  },
  editMap: async (payload) => {
    try {
      get().load()
      await MapService.saveMap(payload)
      SnackbarService.info(
        t('MAPS.SNACK_BAR.EDIT_SUCCESS', {
          name: payload.names.fr,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      await get().list()
    }
  },
  addMap: async (payload) => {
    try {
      get().load()
      await MapService.saveMap(payload)
      SnackbarService.info(
        t('MAPS.SNACK_BAR.ADD_SUCCESS', {
          name: payload.names.fr,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      await get().list()
    }
  },
  delete: async () => {
    try {
      get().load()
      const { current } = get()
      if (current?._id == null) return
      await MapService.deleteMap(current._id)
      SnackbarService.info(t('MAPS.SNACK_BAR.DELETE_SUCCESS'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      await get().list()
      get().setView()
    }
  },
  toggleVisibility: async (payload) => {
    get().load()
    try {
      payload.show = !payload.show
      await MapService.saveMap(payload)
      SnackbarService.info(
        t(payload.show ? 'MAPS.SNACK_BAR.SHOW' : 'MAPS.SNACK_BAR.HIDE', {
          string: payload.names.fr,
        }),
      )
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      await get().list()
      get().setView()
    }
  },
  reset: () => {
    set({ ...new MapsState() })
  },
}))

// --- Thin wrappers preserving the createStore facade API ---
